// Select / autocomplete visibility & enablement rules extracted verbatim from
// components/defaultLayout/EntityDialog&Forms/EntityMaskForm.vue (doDisableWidget,
// autocompleteSearchHasMatches, doAddNewOptionToSelect, isFieldMultiple,
// doShowSelectAll, isSelectAllEnabled, shouldCloseMenuOnAllSelected). Pulled into
// a pure module so these dropdown rules are unit-tested and survive the
// Nuxt 2 -> Nuxt 3 migration; the component now forwards to these. Component
// state read off `this` (inputFields, isViewMode, isEditing, isSubentityDialog,
// per-field search text, processed items) is passed in explicitly.

import type { IEntityMaskData, TEntityMaskItem } from '~/types/models'
import { isEntityMaskData, isTab } from '~/types/models'

function isTabAt(inputFields: TEntityMaskItem[], index: number): boolean {
  const item = inputFields[index]
  return !!item && isTab(item)
}

// A widget is disabled when it is tied to a prior (unchecked) checkbox field, or
// when it declares its own `disabled` flag. Layout-only items are never disabled.
export function doDisableWidget(
  ifield: TEntityMaskItem,
  inputFields: TEntityMaskItem[],
  index: number
): boolean {
  if (!isEntityMaskData(ifield)) {
    return false
  }
  // Step back over a tab marker: it renders nothing, so a checkbox and the field
  // tied to it stay "adjacent" even if a spec puts a tab boundary between them.
  let priorIndex = index - 1
  while (priorIndex >= 0 && isTabAt(inputFields, priorIndex)) {
    priorIndex--
  }
  const priorIField = priorIndex >= 0 ? inputFields[priorIndex] : undefined
  const priorIFieldValue =
    priorIField && isEntityMaskData(priorIField) ? priorIField.value : undefined
  return (
    (ifield.tiedToPriorFieldCheckbox && !priorIFieldValue) ||
    (!ifield.tiedToPriorFieldCheckbox && ifield.disabled !== undefined
      ? ifield.disabled
      : false)
  )
}

// Whether a widget must refuse edits: the dialog is in view mode, or the field
// declares its own `readonly` flag. This is the expression the 15 inline
// `:readonly` bindings in EntityMaskForm's template already carry; it lives here
// because the dateField / timeField widgets need it in more than one place.
//
// ⚠ Those two widgets are why this exists. Their `readonly` attribute is
// HARD-CODED on the activator text field for an unrelated reason -- it stops
// TYPING, so the value can only come from the picker -- and neither block ever
// read `ifield.readonly` or `isViewMode`. So a spec saying `readonly: true`
// still opened the picker on click, and `clearable` still offered an X. A dead
// prop that fails silently: see [[reference_vuetify3_dead_listeners]] for the
// same shape. Layout-only items are never readonly.
export function doMakeWidgetReadonly(
  ifield: TEntityMaskItem,
  isViewMode: boolean
): boolean {
  if (!isEntityMaskData(ifield)) {
    return false
  }
  return isViewMode || (ifield.readonly !== undefined ? ifield.readonly : false)
}

// Whether the current autocomplete search text matches any option -- by the
// text the LIST shows (`_listTitle` where the loader gave one, e.g. a
// location's nickname plus address; else the label), which is what Vuetify's
// own filter searches.
// `searchText` was read off `this.autocompleteSearchTexts[index]` in the original.
export function autocompleteSearchHasMatches(
  ifield: IEntityMaskData,
  searchText: string | undefined
): boolean {
  if (!searchText) {
    return false
  }
  const search = searchText.toLowerCase()
  return (
    ifield.items?.some((item: any) =>
      String(item._listTitle ?? item.label ?? '')
        .toLowerCase()
        .includes(search)
    ) ?? false
  )
}

// Whether to offer the "NEW <subentity>" create option in a select. Not allowed
// inside a nested subentity dialog, requires the field to declare a subentity,
// and (when editing) requires the field to opt in via allowCreateNewSubentityWhenEditing.
export function doAddNewOptionToSelect(
  ifield: IEntityMaskData,
  opts: { isSubentityDialog: boolean; isEditing: boolean }
): boolean | undefined {
  const { isSubentityDialog, isEditing } = opts
  return (
    !isSubentityDialog && // At the moment, not allowing nested subentity 'NEW'
    !!ifield.subentity &&
    (!isEditing || (isEditing && ifield.allowCreateNewSubentityWhenEditing))
  )
}

// A field declared `multiple` renders as single-select when its
// `forceSingleSelectWhen` predicate says so (e.g. all options are kit-products) —
// which removes the per-option checkboxes and Select All. Reads ifield.items via
// the predicate, so it re-evaluates as items load.
export function isFieldMultiple(
  ifield: IEntityMaskData,
  inputFields: TEntityMaskItem[]
): boolean {
  if (!ifield.multiple) {
    return false
  }
  if (
    ifield.forceSingleSelectWhen &&
    ifield.forceSingleSelectWhen(inputFields)
  ) {
    return false
  }
  return true
}

// Whether to render a "Select All" item at the top of a multi-select's dropdown.
// Opt-in per field via `allowSelectAll`; hidden when the field is currently
// rendering single-select (see isFieldMultiple).
export function doShowSelectAll(
  ifield: IEntityMaskData,
  inputFields: TEntityMaskItem[],
  isViewMode: boolean
): boolean {
  return (
    !!ifield.allowSelectAll &&
    isFieldMultiple(ifield, inputFields) &&
    !isViewMode
  )
}

// Whether the "Select All" item is enabled. A field can supply its own predicate
// via `selectAllEnabled`; otherwise it's enabled once something is selected.
// Reads ifield.value during render so it re-evaluates as the selection changes.
export function isSelectAllEnabled(
  ifield: IEntityMaskData,
  inputFields: TEntityMaskItem[]
): boolean {
  return ifield.selectAllEnabled
    ? ifield.selectAllEnabled(inputFields)
    : Array.isArray(ifield.value) && ifield.value.length > 0
}

// For a Select-All field: true once every non-disabled option is selected
// (whether via Select All or manual picks). Selected items are never disabled, so
// value ⊆ eligible. This also covers selecting a lone kit-product — enforcement
// disables the rest, leaving it the only eligible option — so the menu dismisses
// on a kit selection too. `items` is the field's processed items array.
export function shouldCloseMenuOnAllSelected(
  ifield: IEntityMaskData,
  opts: {
    items: object[] | string[] | undefined
    inputFields: TEntityMaskItem[]
    isViewMode: boolean
  }
): boolean {
  const { items, inputFields, isViewMode } = opts
  if (!doShowSelectAll(ifield, inputFields, isViewMode)) {
    return false
  }
  const value = Array.isArray(ifield.value) ? ifield.value : []
  if (value.length === 0) {
    return false
  }
  const eligible = ((items || []) as any[]).filter(
    (i) => i && typeof i === 'object' && !i.disabled
  )
  return eligible.length > 0 && value.length >= eligible.length
}
